import React, { CSSProperties, useEffect, useState } from 'react';

interface ScrollPanelProps {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  backgroundImage?: string;
  pattern?: 'dots' | 'grid' | 'diagonal' | 'circles' | 'lines';
  glassEffect?: boolean;
  darkTheme?: boolean;
  index?: number;
  activeIndex?: number;
  darkOverlay?: boolean;
  primaryColor?: string;
  darkBgColor?: string;
}

const ScrollPanel = ({
  children,
  className = '',
  style = {},
  backgroundImage,
  pattern,
  glassEffect = false,
  darkTheme = false,
  index = 0,
  activeIndex = 0,
  darkOverlay = false,
  primaryColor = '#1db233',
}: ScrollPanelProps) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => setIsVisible(true), []);

  const isActive = index === activeIndex;

  const getPatternStyle = (): CSSProperties => {
    switch (pattern) {
      case 'dots':
        return {
          backgroundImage:
            'radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        };
      case 'grid':
        return {
          backgroundImage:
            'linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        };
      case 'diagonal':
        return {
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03) 2px, transparent 2px, transparent 10px)',
        };
      case 'circles':
        return {
          backgroundImage:
            'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 0, rgba(255, 255, 255, 0.05) 1px, transparent 1.5px), radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.05) 0, rgba(255, 255, 255, 0.05) 1px, transparent 1.5px)',
          backgroundSize: '20px 20px',
        };
      case 'lines':
        return {
          backgroundImage:
            'linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '15px 15px',
        };
      default:
        return {};
    }
  };

  const getGlassStyle = (): CSSProperties =>
    glassEffect
      ? {
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
      }
      : {};

  return (
    <div
      data-index={index}
      data-active={isActive ? 'true' : 'false'}
      className={`
        scroll-panel
        min-w-[90vw] sm:min-w-[450px] lg:min-w-[550px] max-w-[90vw]
        min-h-[520px] md:h-[600px]
        flex-shrink-0 snap-center
        rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500
        p-6 sm:p-8 flex flex-col relative overflow-hidden
        ${isActive ? 'scale-[1.02]' : 'scale-100'}
        ${className}
      `}
      style={{
        ...getPatternStyle(),
        ...getGlassStyle(),
        ...style,
        transitionProperty: 'transform, box-shadow, opacity',
        transitionDuration: '500ms',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isActive
          ? '0 25px 50px -12px rgba(0,0,0,0.5)'
          : '0 10px 30px -15px rgba(0,0,0,0.3)',
        willChange: 'transform',
      }}
    >
      {backgroundImage && (
        <div className="pointer-events-none absolute inset-0 w-full h-full">
          <div
            className="absolute inset-0"
            style={{
              background: darkOverlay
                ? `linear-gradient(135deg, ${primaryColor}cc, #02430eee)`
                : darkTheme
                  ? 'linear-gradient(135deg, rgba(40, 42, 42, 0.9), rgba(40, 42, 42, 0.75))'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.75))',
            }}
          />
          <img
            src={backgroundImage}
            alt=""
            className="w-full h-full object-cover object-center"
            style={{ opacity: darkOverlay ? 0.3 : darkTheme ? 0.3 : 0.15, transition: 'opacity 0.5s ease' }}
          />
        </div>
      )}

      <div
        className={`
          relative z-10 flex flex-col
          h-auto md:h-full
          overflow-y-auto md:overflow-visible
          transition-all duration-1000 transform
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          pb-6 sm:pb-8
        `}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        {children}
      </div>
    </div>
  );
};

export default ScrollPanel;
